import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import type { FieldConfig } from './hooks/useFormErrors';
import { useFormErrors } from './hooks/useFormErrors';

// =============================================================================
// Types
// =============================================================================

export interface FormProviderProps<T extends Record<string, unknown>> {
  /** Field validation config — same shape as useFormValidation's `fields` */
  fields: { [K in keyof T]: FieldConfig };
  /** Current form values */
  values: T;
  /** Human-readable labels for fields, used in error summary */
  fieldLabels?: Partial<Record<keyof T & string, string>>;
  children: ReactNode;
}

interface FieldProps {
  onBlur: () => void;
  error: string | undefined;
  showError: boolean;
}

export interface FormContextValue {
  /** Get props to spread onto a field: { onBlur, error, showError } */
  getFieldProps: (name: string) => FieldProps;
  /** Mark all fields as touched (call on submit attempt) */
  touchAll: () => void;
  /** Remove a field from the touched set (useful when removing dynamic fields) */
  resetField: (name: string) => void;
  /** Clear all touched state (useful for form reset / create-another flows) */
  resetTouched: () => void;
  /** Whether all fields pass validation (regardless of touched state) */
  isValid: boolean;
  /** All errors (touched or not) */
  errors: Record<string, string | undefined>;
  /** Only errors for touched fields */
  visibleErrors: Record<string, string | undefined>;
  /** Touched field set */
  touched: ReadonlySet<string>;
  /** List of errored fields with human-readable labels and messages, for submit button */
  errorSummary: Array<{ name: string; label: string; error: string }>;
  /** Scroll to a field's DOM element and focus its first input */
  scrollToField: (name: string) => void;
  /** Mark all fields belonging to a step (or global fields) as touched */
  touchStep: (step: number) => void;
  /** Whether all fields for a step (and global fields) pass validation */
  isStepValid: (step: number) => boolean;
  /** Error summary filtered to fields belonging to a step (and global fields) */
  stepErrorSummary: (
    step: number
  ) => Array<{ name: string; label: string; error: string }>;
  /** API/submission error message — disables submit and shows in tooltip. Clears on form value change. */
  submitError: string | null;
  /** Set an API/submission error after a failed submit. Pass null to clear. */
  setSubmitError: (error: string | null) => void;
}

// =============================================================================
// Context
// =============================================================================

const FormContext = createContext<FormContextValue | null>(null);

// eslint-disable-next-line react-refresh/only-export-components
export function useFormContext(): FormContextValue {
  const ctx = useContext(FormContext);
  if (!ctx) {
    throw new Error('useFormContext must be used within a FormProvider');
  }
  return ctx;
}

// =============================================================================
// Helpers
// =============================================================================

/** Capitalize a field name and replace separators with spaces for fallback labels. */
function humanizeFieldName(name: string): string {
  // Strip composite key prefix (e.g. "abc123:name" → "name")
  const parts = name.split(':');
  const field = parts[parts.length - 1];
  return field.replace(/[-_]/g, ' ').replace(/^\w/, c => c.toUpperCase());
}

// =============================================================================
// FormProvider
// =============================================================================

export function FormProvider<T extends Record<string, unknown>>({
  fields,
  values,
  fieldLabels,
  children,
}: FormProviderProps<T>) {
  const [touched, setTouched] = useState<Set<string>>(new Set());
  const { errors, isValid: fieldsValid } = useFormErrors({ fields, values });

  // API/submission error state — auto-clears when form values change
  const [submitError, setSubmitError] = useState<string | null>(null);
  const isFirstRender = useRef(true);
  useEffect(() => {
    // Skip the initial render — only clear on actual value changes
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (submitError) {
      setSubmitError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);

  const isValid = fieldsValid && !submitError;

  const visibleErrors = useMemo<Record<string, string | undefined>>(() => {
    const result: Record<string, string | undefined> = {};
    for (const name of Object.keys(errors)) {
      if (touched.has(name)) {
        result[name] = errors[name];
      }
    }
    return result;
  }, [errors, touched]);

  const errorSummary = useMemo<
    Array<{ name: string; label: string; error: string }>
  >(() => {
    const summary: Array<{ name: string; label: string; error: string }> = [];
    for (const [name, error] of Object.entries(errors)) {
      if (error) {
        const label =
          fieldLabels?.[name as keyof T & string] ?? humanizeFieldName(name);
        summary.push({ name, label, error });
      }
    }
    return summary;
  }, [errors, fieldLabels]);

  const touchAll = useCallback(() => {
    setTouched(new Set(Object.keys(fields)));
  }, [fields]);

  const resetField = useCallback((name: string) => {
    setTouched(prev => {
      if (!prev.has(name)) {
        return prev;
      }
      const next = new Set(prev);
      next.delete(name);
      return next;
    });
  }, []);

  const resetTouched = useCallback(() => {
    setTouched(new Set());
  }, []);

  const scrollToField = useCallback((name: string) => {
    const el = document.querySelector(`[data-form-field="${name}"]`);
    if (!el) {
      return;
    }
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // Focus the first focusable input inside the field wrapper
    const focusable = el.querySelector<HTMLElement>(
      'input, select, textarea, [tabindex]'
    );
    if (focusable) {
      // Delay focus to after scroll animation starts
      requestAnimationFrame(() => focusable.focus());
    }
  }, []);

  const getFieldProps = useCallback(
    (name: string): FieldProps => {
      const error = errors[name];
      return {
        onBlur: () => {
          setTouched(prev => {
            if (prev.has(name)) {
              return prev;
            }
            const next = new Set(prev);
            next.add(name);
            return next;
          });
        },
        error,
        showError: touched.has(name) && !!error,
      };
    },
    [errors, touched]
  );

  const touchStep = useCallback(
    (step: number) => {
      setTouched(prev => {
        const next = new Set(prev);
        for (const name of Object.keys(fields)) {
          if (
            fields[name as keyof T].step === step ||
            fields[name as keyof T].step === undefined
          ) {
            next.add(name);
          }
        }
        return next;
      });
    },
    [fields]
  );

  const isStepValid = useCallback(
    (step: number): boolean => {
      return Object.keys(fields)
        .filter(
          name =>
            fields[name as keyof T].step === step ||
            fields[name as keyof T].step === undefined
        )
        .every(name => !errors[name]);
    },
    [fields, errors]
  );

  const stepErrorSummary = useCallback(
    (step: number): Array<{ name: string; label: string; error: string }> => {
      return errorSummary.filter(
        entry =>
          fields[entry.name as keyof T]?.step === step ||
          fields[entry.name as keyof T]?.step === undefined
      );
    },
    [errorSummary, fields]
  );

  const contextValue = useMemo<FormContextValue>(
    () => ({
      getFieldProps,
      touchAll,
      resetField,
      resetTouched,
      isValid,
      errors,
      visibleErrors,
      touched,
      errorSummary,
      scrollToField,
      touchStep,
      isStepValid,
      stepErrorSummary,
      submitError,
      setSubmitError,
    }),
    [
      getFieldProps,
      touchAll,
      resetField,
      resetTouched,
      isValid,
      errors,
      visibleErrors,
      touched,
      errorSummary,
      scrollToField,
      touchStep,
      isStepValid,
      stepErrorSummary,
      submitError,
    ]
  );

  return (
    <FormContext.Provider value={contextValue}>{children}</FormContext.Provider>
  );
}
